import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Grid, Select, MenuItem, FormControl, Switch, FormControlLabel, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, IconButton, Menu } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import getSymbolFromCurrency from "currency-symbol-map";
import { planService, api } from "../../../api";
import { ApiConfig } from "../../../api/ApiConfig";
import { TOPUP_ATTENDANCE_ALLOWANCE_KEYS, UNLIMITED_LABEL } from "../../../constants/planConstants";
import styles from "./style";

const ViewClientPage = ({ clientData, transactions = [] }) => {
  const navigate = useNavigate();
  const options = countryList().getData();
  const [planList, setPlanList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleMenuOpen = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const handleDownloadInvoice = async () => {
    if (!selectedTransaction) return;
    try {
      const transactionId = selectedTransaction.id || selectedTransaction._id;
      const token = localStorage.getItem("accessToken");
      const headers = { Authorization: `Bearer ${token}` };
      const url = `${ApiConfig.baseApi}transaction_history_pdf/${transactionId}?mode=download`;
      
      const response = await api.get(url, null, headers, "blob");
      
      if (response) {
        const blob = new Blob([response], { type: "application/pdf" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Invoice_${transactionId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Failed to download invoice.");
    }
    handleMenuClose();
  };

  const handleSendInvoiceEmail = async () => {
    if (!selectedTransaction) return;
    try {
      const transactionId = selectedTransaction.id || selectedTransaction._id;
      const token = localStorage.getItem("accessToken");
      const headers = { Authorization: `Bearer ${token}` };
      const url = `${ApiConfig.baseApi}transaction_history_pdf/${transactionId}?mode=send`;
      
      await api.get(url, null, headers);
      alert("Invoice sent successfully!");
    } catch (error) {
      console.error("Error sending invoice:", error);
      alert("Failed to send invoice.");
    }
    handleMenuClose();
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const result = await planService.list();
      const normalizeListResponse = (response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.result)) return response.result;
        return [];
      };
      const plans = normalizeListResponse(result);
      setPlanList(plans || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const [formData, setFormData] = useState({
    companyName: "",
    companyMailingAddress: "",
    address1: "",
    owner: "",
    phoneNumber: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    industry: "",
    plan: "",
    billingCycle: "",
    startDate: dayjs(),
    adminEmail: "",
    isActive: true,
    planPrice: "",
    planCurrency: "",
    planExpiryDate: null,
    moduleAccessOverrides: {},
    moduleLimitOverrides: {},
    usage: {},
    effectiveAccess: {},
    effectiveLimits: {},
    moduleAccess: {},
    moduleLimits: {},
  });

  useEffect(() => {
    if (clientData) {
      setFormData({
        companyName: clientData.companyName || "",
        companyMailingAddress: clientData.companyMailingAddress || "",
        address1: clientData.address1 || "",
        owner: clientData.owner || "",
        phoneNumber: clientData.phoneNumber || "",
        city: clientData.city || "",
        state: clientData.state || "",
        zipCode: clientData.zipCode || "",
        country: clientData.country || "",
        industry: clientData.industry || "",
        plan: clientData.plan || "",
        billingCycle: clientData.billingCycle || "",
        startDate: clientData.startDate || dayjs(),
        adminEmail: clientData.adminEmail || "",
        isActive: clientData.isActive !== undefined ? clientData.isActive : true,
        planPrice: clientData.planPrice || "",
        planCurrency: clientData.planCurrency || "",
        planExpiryDate: clientData.planExpiryDate || null,
        moduleAccessOverrides: clientData.moduleAccessOverrides || {},
        moduleLimitOverrides: clientData.moduleLimitOverrides || {},
        usage: clientData.usage || {},
        effectiveAccess: clientData.effectiveAccess || {},
        effectiveLimits: clientData.effectiveLimits || {},
        moduleAccess: clientData.moduleAccess || {},
        moduleLimits: clientData.moduleLimits || {},
      });
    }
  }, [clientData]);

  const selectedPlan = planList.find(p => p._id === formData.plan) || {};
  const planName = selectedPlan.planName || formData.planName || "";

  const formatTransactionDate = (value) => {
    if (!value) return "-";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
  };

  const formatPriceWithCurrency = (price, currency) => {
    if (price === "-" || price === null || price === undefined || price === "") return "-";
    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice)) return `${currency || ""} ${price}`.trim();
    return `${currency || ""} ${numericPrice}`.trim();
  };

  const planCurrencyCode = String(formData.planCurrency || "").toUpperCase();
  const planCurrencySymbol = planCurrencyCode ? getSymbolFromCurrency(planCurrencyCode) : "";
  const planPriceAdornment = planCurrencySymbol || planCurrencyCode || "";

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>View Client</Typography>
        <Typography sx={styles.subtitle}>View client information and subscription details.</Typography>
      </Box>
        <form>
          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Company Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Company Name"
                  value={formData.companyName}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Owner Name"
                  value={formData.owner}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Company Email Address"
                  value={formData.companyMailingAddress}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Address Line 1"
                  value={formData.address1}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={styles.phoneInputContainer}>
                  <PhoneInput
                    country={"za"}
                    enableSearch={true}
                    value={formData.phoneNumber}
                    disabled={true}
                    inputProps={{
                      name: "phoneNumber",
                      readOnly: true,
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "42px",
                      borderRadius: "8px",
                      backgroundColor: "#f5f5f5",
                      color: "#000000",
                      cursor: "not-allowed",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="City"
                  value={formData.city}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="State"
                  value={formData.state}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Zip Code"
                  value={formData.zipCode}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Select
                    value={formData.country}
                    displayEmpty
                    renderValue={(selected) =>
                      selected && selected !== "" ? selected : "Country"
                    }
                    disabled
                    sx={{ ...styles.select, backgroundColor: "#f5f5f5" }}
                  >
                    <MenuItem value="" disabled>Country</MenuItem>
                    {options.map((option, index) => (
                      <MenuItem key={index} value={option.label}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Industry"
                  value={formData.industry}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={styles.section}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
              <Typography sx={{ ...styles.sectionTitle, mb: 0 }}>Subscription Plan</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    disabled
                    sx={styles.switch}
                  />
                }
                label="Active Subscription"
                sx={{ m: 0 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <Select
                    value={planName || formData.plan}
                    displayEmpty
                    disabled
                    sx={{ ...styles.select, backgroundColor: "#f5f5f5" }}
                  >
                    <MenuItem value="" disabled>Plan</MenuItem>
                    {planList.map((plan) => (
                      <MenuItem key={plan._id} value={plan.planName}>
                        {plan.planName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <Select
                    value={formData.billingCycle || ""}
                    displayEmpty
                    disabled
                    sx={{ ...styles.select, backgroundColor: "#f5f5f5" }}
                  >
                    <MenuItem value="" disabled>Billing Cycle</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.startDate}
                    format="DD-MM-YYYY"
                    disabled
                    sx={styles.datePicker}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "dd-mm-yyyy",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Price"
                  value={formData.planPrice}
                  InputProps={{
                    readOnly: true,
                    startAdornment: planPriceAdornment ? (
                      <Typography sx={{ mr: 1, fontSize: "14px" }}>{planPriceAdornment}</Typography>
                    ) : null,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Plan Expiry Date"
                    value={formData.planExpiryDate}
                    format="DD-MM-YYYY"
                    disabled
                    sx={styles.datePicker}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "dd-mm-yyyy",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>

          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Module Usage & Limits</Typography>
            {[
              { key: "companies", label: "Companies", hideToggle: true, limits: [{ key: "companiesPerPlan", label: "Limit" }] },
              { key: "operators", label: "Operators", limits: [{ key: "operatorsPerPlan", label: "Limit" }] },
              { key: "invoicing", label: "Invoicing", limits: [{ key: "invoicesPerPlan", label: "Limit" }, { key: "invoiceValueCap", label: "Value Cap" }] },
              { key: "manageEmployees", label: "Users", limits: [{ key: "activeEmployees", label: "Limit" }] },
              { key: "attendance", label: "Attendance", limits: [
                { key: "attendanceDaysPerPlan", label: "Days / Plan" },
                { key: "attendanceUsersPerPeriod", label: "Users / Period" },
              ] },
              { key: "loans", label: "Loans", limits: [{ key: "activeLoans", label: "Limit" }] },
              { key: "deductions", label: "Deductions", limits: [{ key: "activeDeductions", label: "Limit" }] },
              { key: "leaveManagement", label: "Leave Management", limits: [{ key: "leaveRequestsPerPlan", label: "Limit" }] },
              { key: "commission", label: "Commission", limits: [{ key: "commissionRunsPerPlan", label: "Limit" }] },
              { key: "travelClaims", label: "Travel Claims & Allowances", limits: [{ key: "travelClaimsPerPlan", label: "Claims" }, { key: "allowancesPerPlan", label: "Allowances" }] },
              { key: "recruitment", label: "Recruitment", limits: [{ key: "recruitmentPerPlan", label: "Limit" }] },
              { key: "timesheet", label: "Timesheet", limits: [{ key: "timesheetPerPlan", label: "Limit" }] },
              { key: "projects", label: "Projects", limits: [{ key: "activeProjects", label: "Limit" }] },
              { key: "payroll", label: "Payroll", limits: [{ key: "payrollRunsPerPlan", label: "Limit" }] },
              { key: "salaryManagement", label: "Salary Management", limits: [{ key: "payslipLimit", label: "Limit" }] },
              { key: "reports", label: "Reports", limits: [
                { key: "reportDownloadsPerPlan", label: "Limit" },
                { key: "exportFormats", label: "Export formats", type: "select" }
              ] },
              { key: "documentsUploadPerEmployee", label: "Document Uploads (Per Employee)", hideToggle: true, limits: [{ key: "employeeDocsPerEmployee", label: "Limit" }] },
            ].map((module) => {
              // hideToggle rows mirror the plan form: they have no access flag, so they are always on.
              const isEnabled = module.hideToggle || (formData.moduleAccessOverrides[module.key] ?? formData.moduleAccess[module.key] ?? formData.effectiveAccess[module.key] ?? false);

              return (
                <Box key={module.key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f0f0f0" }}>
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    {module.hideToggle ? (
                      <Box sx={{ width: "40px" }} />
                    ) : (
                      <Switch
                        size="small"
                        checked={isEnabled}
                        disabled
                      />
                    )}
                    <Typography sx={{ ml: 1, fontSize: "15px", fontWeight: 500, color: isEnabled ? "text.primary" : "text.disabled" }}>{module.label}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {module.limits.map((limit) => {
                      // Read the raw value before the `?? ""` collapse: for the two
                      // attendance keys, an undefined/missing value across every
                      // source means "no cap" and must render as "Unlimited," not
                      // as a blank/0 input — every other limit keeps the old
                      // blank-TextField fallback.
                      const rawLimitValue =
                        formData.moduleLimitOverrides[limit.key] ??
                        formData.moduleLimits[limit.key] ??
                        formData.effectiveLimits[limit.key];
                      const isUnlimitedAttendance =
                        TOPUP_ATTENDANCE_ALLOWANCE_KEYS.includes(limit.key) &&
                        (rawLimitValue === null || rawLimitValue === undefined);
                      const limitValue = rawLimitValue ?? "";
                      const usedValue = formData.usage[limit.key]?.used ?? formData.usage[module.key]?.used ?? 0;

                      return (
                        <Box key={limit.key} sx={{ display: "flex", gap: 2 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "13px" }}>{limit.label}</Typography>
                            {limit.type === "select" ? (
                              <TextField
                                size="small"
                                value={Array.isArray(limitValue) ? limitValue.join(", ") : limitValue}
                                disabled
                                sx={{ width: "150px", "& .MuiInputBase-input": { py: 0.5, fontSize: "14px", backgroundColor: "#f9f9f9" } }}
                              />
                            ) : isUnlimitedAttendance ? (
                              <TextField
                                size="small"
                                value={UNLIMITED_LABEL}
                                disabled
                                sx={{ width: "100px", "& .MuiInputBase-input": { py: 0.5, fontSize: "14px", backgroundColor: "#f9f9f9", fontStyle: "italic" } }}
                              />
                            ) : (
                              <TextField
                                size="small"
                                value={limitValue}
                                disabled
                                sx={{ width: "100px", "& .MuiInputBase-input": { py: 0.5, fontSize: "14px", backgroundColor: "#f9f9f9" } }}
                              />
                            )}
                          </Box>
                          {limit.type !== "select" && (
                            <Box>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "13px" }}>Used</Typography>
                              <TextField
                                size="small"
                                value={usedValue}
                                disabled
                                sx={{ width: "100px", "& .MuiInputBase-input": { py: 0.5, fontSize: "14px", backgroundColor: "#f9f9f9" } }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Client Admin Login</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Admin Email (Login Username)"
                  value={formData.adminEmail}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={styles.infoText}>
                  These credentials are used by the client Super Admin to access the platform.
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Transaction History</Typography>
            <TableContainer component={Paper} sx={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.tableHeaderCell}>Plan Name</TableCell>
                    <TableCell sx={styles.tableHeaderCell}>Billing Cycle</TableCell>
                    <TableCell sx={styles.tableHeaderCell}>Price</TableCell>
                    <TableCell sx={styles.tableHeaderCell}>Date</TableCell>
                    <TableCell sx={styles.tableHeaderCell}>Status</TableCell>
                    <TableCell sx={styles.tableHeaderCell} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={styles.emptyCell}>
                        <Typography>No transaction history found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((item) => (
                      <TableRow key={item.id} sx={styles.tableRow}>
                        <TableCell sx={styles.tableCell}>{item.planName || "-"}</TableCell>
                        <TableCell sx={styles.tableCell}>{item.billingCycle || "-"}</TableCell>
                        <TableCell sx={styles.tableCell}>
                          {formatPriceWithCurrency(item.price, item.currency)}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>{formatTransactionDate(item.date)}</TableCell>
                        <TableCell sx={styles.tableCell}>{item.status || "-"}</TableCell>
                        <TableCell sx={styles.tableCell} align="center">
                          <IconButton onClick={(e) => handleMenuOpen(e, item)} size="small">
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                  }
                }}
              >
                <MenuItem onClick={handleDownloadInvoice} sx={{ fontSize: "14px", py: 1, px: 2 }}>
                  Download PDF
                </MenuItem>
                <MenuItem onClick={handleSendInvoiceEmail} sx={{ fontSize: "14px", py: 1, px: 2 }}>
                  Send Invoice
                </MenuItem>
              </Menu>
            </TableContainer>
          </Box>

          <Box sx={styles.buttonContainer}>
            <LoadingButton
              variant="outlined"
              onClick={() => navigate("/dashboard/userManagement")}
              sx={styles.cancelButton}
            >
              Back
            </LoadingButton>
          </Box>
        </form>
    </Box>
  );
};

export default ViewClientPage;
